"use client";

import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Checkbox, Field, FormStatus, Input, Radio, Select, SubmitButton, Textarea } from "@/components/ui/Form";
import { Icon } from "@/lib/icons/Icon";
import { useDictionary, type DictionaryType } from "@/lib/api/dictionary";
import {
  fetchOrderForm,
  lookupOrderCompany,
  submitOrder,
  OrderApiError,
  ORDER_FIELD_DATE,
  ORDER_FIELD_DIGIT,
  ORDER_FIELD_NUMBER,
  ORDER_FIELD_PRICE,
  ORDER_FIELD_TEXTAREA,
  ORDER_FIELD_URL,
  type OrderAccountType,
  type OrderFormData,
  type OrderFormField,
  type OrderResult,
  type OrderServiceField,
} from "@/lib/api/c-crm-project-management-order";
import type { CCrmProjectManagementTranslations } from "./CCrmProjectManagement.i18n";
import styles from "./CCrmProjectManagement.module.css";

/**
 * Pojedyncze pole dodatkowe pakietu w formularzu zamówienia.
 *
 * Kontrolka zależy od typu pola z definicji pakietu, a opis idzie POZA `Field`: pływająca
 * etykieta pozycjonuje się na połowie wysokości całego pola, więc podpowiedź w środku
 * `Field` zepchnęłaby ją pod input. Z tego samego powodu nie ustawiamy własnego
 * `placeholder` - przykładowa wartość ląduje w podpowiedzi.
 */
function ServiceFieldInput({
  field,
  value,
  error,
  onValue,
  t,
}: {
  field: OrderServiceField;
  value: string;
  error?: string;
  onValue: (id: number, value: string) => void;
  t: CCrmProjectManagementTranslations;
}) {
  const hint = [
    field.description,
    field.placeholder ? `${t.orderFieldExample} ${field.placeholder}` : "",
    field.min_value !== null ? t.orderFieldMinHint.replace("{min}", formatMinValue(field.min_value)) : "",
  ]
    .filter(Boolean)
    .join(" ");

  const numeric = field.type === ORDER_FIELD_PRICE || field.type === ORDER_FIELD_NUMBER;
  const digit = field.type === ORDER_FIELD_DIGIT;

  return (
    <>
      <Field label={field.name} required={field.required} error={error}>
        {field.type === ORDER_FIELD_TEXTAREA ? (
          <Textarea
            rows={4}
            value={value}
            invalid={!!error}
            onChange={(e) => onValue(field.id, e.target.value)}
          />
        ) : (
          <Input
            /* Typ `date` daje natywny kalendarz i format RRRR-MM-DD, którego oczekuje serwer.
               Liczby zostają tekstem: `type="number"` w przeglądarce odrzuca przecinek
               dziesiętny, a serwer go przyjmuje - klient wpisywałby wartość, która znika. */
            type={field.type === ORDER_FIELD_DATE ? "date" : "text"}
            inputMode={numeric ? "decimal" : digit ? "numeric" : field.type === ORDER_FIELD_URL ? "url" : undefined}
            autoComplete={field.type === ORDER_FIELD_URL ? "url" : undefined}
            value={value}
            invalid={!!error}
            onChange={(e) => onValue(field.id, e.target.value)}
          />
        )}
      </Field>
      {hint && <p className={styles.orderHint}>{hint}</p>}
    </>
  );
}

/** Sekcje formularza - kolejnosc taka, w jakiej sie go wypelnia. */
const SECTIONS: OrderFormField["section"][] = ["contact", "company", "billing"];

/**
 * Czy tekst wyglada na adres strony.
 *
 * Reguła jest kopią serwerowej (`M_SERVICES_MCONTROLLER::isWebAddressValid`): brakujący schemat
 * dopisujemy (`https://`), więc „mojafirma.pl" przechodzi tak samo jak „https://mojafirma.pl",
 * a host musi mieć kropkę i końcówkę z samych liter.
 *
 * Celowo BEZ `new URL()`: parser adresów przeglądarki po cichu naprawia błędny zapis, więc
 * „https:///www.agendo.pl" (trzy ukośniki) widzi jako poprawny adres www.agendo.pl i taką
 * wartość klient wysyłał do zamówienia. Rozbieramy adres sami, wzorcem - schemat musi być
 * zakończony DOKŁADNIE dwoma ukośnikami.
 */
function isValidWebAddress(value: string): boolean {
  const raw = value.trim();
  if (!raw || /\s/.test(raw)) return false;

  const scheme = raw.match(/^([a-z][a-z0-9+.-]*):\/\//i);
  //monitoring sprawdza stronę WWW, więc inne schematy (ftp, mailto, javascript) odpadają
  if (scheme && !/^https?$/i.test(scheme[1])) return false;

  const rest = scheme ? raw.slice(scheme[0].length) : raw;
  //część adresowa kończy się na ścieżce, parametrach albo kotwicy
  const authority = rest.split(/[/?#]/)[0];
  const host = authority.replace(/^[^@]*@/, "").replace(/:\d+$/, "");

  /* Litery spoza ASCII przepuszczamy (domena z polskimi znakami) - serwer sprowadza taki host
     do punycode i to on ma ostatnie słowo. Blokowanie tutaj odrzucałoby poprawny adres. */
  return /^(?:[a-z0-9\u00a1-\uffff](?:[a-z0-9\u00a1-\uffff-]*[a-z0-9\u00a1-\uffff])?\.)+[a-z\u00a1-\uffff]{2,}$/i.test(
    host,
  );
}

/** Liczba w zapisie, który przyjmuje serwer - przecinek dziesiętny jest dozwolony. */
function isDecimal(value: string): boolean {
  return /^-?[0-9]+([.,][0-9]+)?$/.test(value);
}

/** Data kalendarzowa w formacie RRRR-MM-DD (odrzuca 2026-02-31). */
function isCalendarDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

/** Granica z definicji pola w postaci pokazywanej człowiekowi (bez zbędnych zer). */
function formatMinValue(min: number): string {
  return String(min);
}

/**
 * Sprawdzenie wartosci dodatkowego pola pakietu.
 *
 * Reguła jest kopią serwerowej (`M_SERVICES_MCONTROLLER::validateServiceFieldValue`), żeby
 * klient dowiadywał się o błędzie od razu, a nie dopiero z odpowiedzi backendu. Serwer
 * sprawdza to samo jeszcze raz - walidację w przeglądarce da się ominąć.
 *
 * @returns komunikat błędu albo null, gdy wartość jest poprawna
 */
function validateServiceField(
  field: OrderServiceField,
  raw: string,
  t: CCrmProjectManagementTranslations,
): string | null {
  const value = raw.trim();

  if (!value) return field.required ? t.orderFieldRequired : null;

  const belowMin =
    field.min_value !== null && Number(value.replace(",", ".")) < field.min_value
      ? t.orderFieldBelowMin.replace("{min}", formatMinValue(field.min_value))
      : null;

  switch (field.type) {
    case ORDER_FIELD_DATE:
      return isCalendarDate(value) ? null : t.orderFieldInvalidDate;
    case ORDER_FIELD_PRICE:
    case ORDER_FIELD_NUMBER:
      if (!isDecimal(value)) return t.orderFieldInvalidNumber;
      return belowMin;
    case ORDER_FIELD_DIGIT:
      if (!/^-?[0-9]+$/.test(value)) return t.orderFieldInvalidDigit;
      return belowMin;
    case ORDER_FIELD_URL:
      return isValidWebAddress(value) ? null : t.orderFieldInvalidUrl;
    default:
      //tekst i obszar tekstowy - wystarczy, że cokolwiek wpisano
      return null;
  }
}

/**
 * Formularz zamowienia uslugi - drugi krok publicznej strony z oferta.
 *
 * **Zrodlo prawdy o formularzu jest po stronie PHP.** Zestaw pol przychodzi z
 * konfiguracji pol grupy uzytkownikow wybranego wariantu konta (firma / osoba
 * prywatna), a nie z kodu frontu - inaczej zmiana konfiguracji grupy w panelu
 * rozjezdzalaby sie z tym, co widzi klient.
 *
 * **Trzy zakonczenia sciezki**, wszystkie rozstrzyga backend:
 *   - bezplatny test - usluga rusza od razu, bez dokumentu,
 *   - pakiet bezplatny - jak wyzej, pro forma na zero zlotych nie ma sensu,
 *   - pakiet platny - pro forma z platnoscia online; usluga powstaje dopiero po
 *     zaksiegowaniu wplaty, wiec front przenosi klienta pod adres platnosci.
 *
 * **Logowanie w trakcie** nie jest tu dublowane - formularz wola istniejace
 * `/api/iiapi/login`, a po udanym logowaniu przeladowuje wlasna definicje. Dzieki
 * temu sesja powstaje dokladnie tak samo jak przy logowaniu ze strony konta.
 *
 * **Walidacja jest reczna, bez react-hook-form/zod** (odstepstwo od `TaskForm` i
 * `CContactForm`): nazwy i komplet pol zmieniaja sie razem z wyborem wariantu
 * konta, wiec schemat trzeba by przebudowywac i resetowac formularz przy kazdym
 * przelaczeniu. Reguly i tak egzekwuje serwer - tutejsze sprawdzenie jest wygoda,
 * a nie zabezpieczeniem.
 *
 * **Security:** `regulamin.text` to HTML przepuszczony przez sanitizer komponentu
 * po stronie PHP - dlatego `dangerouslySetInnerHTML`. Etykiety pol i nazwy
 * projektow to plain text (React je escapuje).
 */
export function OrderForm({ idPackage, currency, t, onBack }: OrderFormProps) {
  const [data, setData] = useState<OrderFormData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [accountType, setAccountType] = useState<"company" | "private" | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [regulaminAccepted, setRegulaminAccepted] = useState(false);
  const [regulaminOpen, setRegulaminOpen] = useState(false);
  const [regulaminError, setRegulaminError] = useState<string | null>(null);
  //wartości pól dodatkowych pakietu, kluczem jest ID definicji pola
  const [serviceValues, setServiceValues] = useState<Record<number, string>>({});
  const [serviceErrors, setServiceErrors] = useState<Record<number, string>>({});
  const serviceFields = data?.service_fields ?? [];

  const setServiceValue = (id: number, value: string) => {
    setServiceValues((prev) => ({ ...prev, [id]: value }));
    setServiceErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const [trial, setTrial] = useState(false);
  const [projectChoice, setProjectChoice] = useState("new");

  const [loginOpen, setLoginOpen] = useState(false);
  const [loginValues, setLoginValues] = useState({ login: "", password: "" });
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);

  const [gusBusy, setGusBusy] = useState(false);
  const [gusMessage, setGusMessage] = useState<string | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [result, setResult] = useState<OrderResult | null>(null);

  /* Wczytanie definicji formularza. Bump `reloadToken` powtarza je po zalogowaniu -
     backend zwraca wtedy dane klienta i liste jego projektow. Przeladowanie jest
     ciche: `loading` juz nie wraca na `true`, wiec wypelniony formularz nie znika
     na moment pod komunikatem o wczytywaniu. */
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const res = await fetchOrderForm(idPackage);
        if (cancelled) return;
        setData(res);
        setAccountType((prev) => prev ?? res.account_types[0]?.key ?? null);
        setLoadError(null);

        /* Dane z konta nadpisują to, co gość zdążył wpisać przed zalogowaniem - po
           zalogowaniu to konto jest źródłem prawdy o danych do faktury. Pola, których
           konto nie wypełnia, zostają z tym, co wpisał. */
        const prefill: Record<string, string> = {};
        for (const field of res.fields) if (field.value) prefill[field.name] = field.value;
        if (Object.keys(prefill).length > 0) setValues((prev) => ({ ...prev, ...prefill }));
      } catch (e) {
        if (!cancelled) setLoadError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [idPackage, reloadToken]);

  const pkg = data?.package ?? null;

  const activeType: OrderAccountType | null = useMemo(() => {
    if (!data || data.logged_in) return null;
    return data.account_types.find((a) => a.key === accountType) ?? data.account_types[0] ?? null;
  }, [data, accountType]);

  /* Zalogowany dostaje pola swojego konta (z danymi), gość - pola wariantu, który
     wybrał. Jedna lista dla walidacji, renderu i wysyłki, więc nie ma jak się
     rozjechać między tym, co widać, a tym, co idzie na serwer. */
  const fields = useMemo(
    () => (data?.logged_in ? data.fields : (activeType?.fields ?? [])),
    [data, activeType],
  );

  /** Zmiana wariantu konta zostawia wpisane wartosci - pola powtarzajace sie
      miedzy wariantami (imie, e-mail) nie kaza wpisywac ich drugi raz. */
  const setValue = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const onLogin = async () => {
    setLoginError(null);
    setLoggingIn(true);
    try {
      const res = await fetch("/api/iiapi/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ login: loginValues.login.trim(), password: loginValues.password }),
      });
      const body = (await res.json().catch(() => null)) as
        | { ok?: boolean; mfa_required?: boolean; message?: string }
        | null;

      /* Logowanie dwuskladnikowe ma wlasny, osobny przebieg na stronie konta -
         nie powtarzamy go tutaj, tylko mowimy wprost co zrobic. */
      if (body?.mfa_required) {
        setLoginError(t.orderLoginMfa);
        return;
      }
      if (!res.ok || body?.ok !== true) {
        setLoginError(body?.message ?? t.orderLoginFailed);
        return;
      }

      setLoginOpen(false);
      setLoginValues({ login: "", password: "" });
      setReloadToken((v) => v + 1);
    } catch (e) {
      setLoginError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoggingIn(false);
    }
  };

  /**
   * Pobranie danych firmy z GUS po NIP-ie wpisanym w formularzu.
   *
   * Wypełnia tylko te pola, które rejestr zna - reszta zostaje z tym, co wpisał klient.
   * Nadpisujemy też pola już wypełnione: skoro ktoś świadomie klika "pobierz z GUS",
   * chce wersji z rejestru, a nie sklejki obu.
   */
  const onGusLookup = async () => {
    const nip = values.billing_nip?.trim() ?? "";

    setGusMessage(null);

    if (!nip) {
      setFieldErrors((prev) => ({ ...prev, billing_nip: t.orderGusNipRequired }));
      return;
    }

    setGusBusy(true);
    try {
      const res = await lookupOrderCompany(nip);

      if (!res.found) {
        setGusMessage(res.message || t.orderGusNotFound);
        return;
      }

      setValues((prev) => ({ ...prev, ...res.fields }));
      setFieldErrors({});
      setGusMessage(t.orderGusFilled);
    } catch (e) {
      setGusMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setGusBusy(false);
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!data || !pkg) return;

    setSubmitError(null);

    const errors: Record<string, string> = {};

    for (const field of fields) {
      if (!field.required || values[field.name]?.trim()) continue;
      //adres jest kluczem konta i celem powiadomien - warto powiedziec o tym wprost
      errors[field.name] = field.name === "email" ? t.orderEmailRequired : t.orderFieldRequired;
    }

    /* Pola dodatkowe wymagane przez pakiet. Backend sprawdza to samo, ale bez tej walidacji
       klient dowiadywałby się o błędzie dopiero po wysłaniu całego formularza razem z danymi
       rozliczeniowymi - a formularz wysyła się przez JS, więc walidacja przeglądarki
       (`required`, `type="url"`) w ogóle nie rusza. */
    const serviceFieldErrors: Record<number, string> = {};

    for (const field of serviceFields) {
      const error = validateServiceField(field, serviceValues[field.id] ?? "", t);
      if (error) serviceFieldErrors[field.id] = error;
    }

    setServiceErrors(serviceFieldErrors);

    const regulaminMissing = !!data.regulamin && !regulaminAccepted;
    setRegulaminError(regulaminMissing ? t.orderRegulaminRequired : null);
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0 || regulaminMissing || Object.keys(serviceFieldErrors).length > 0) return;

    setSubmitting(true);
    try {
      /* Wysyłamy WYŁĄCZNIE pola z aktualnego zestawu - po przełączeniu wariantu konta
         w stanie zostają wartości pól, których drugi wariant nie ma, a backend odrzuca
         nazwy spoza konfiguracji grupy. */
      const payload: Record<string, string> = {};
      for (const field of fields) payload[field.name] = values[field.name] ?? "";

      const res = await submitOrder({
        id_package: pkg.id,
        fields: payload,
        ...(data.logged_in ? {} : { account_type: activeType?.key }),
        ...(data.regulamin ? { regulamin_accept: true } : {}),
        ...(serviceFields.length
          ? {
              service_fields: Object.fromEntries(
                serviceFields.map((field) => [String(field.id), (serviceValues[field.id] ?? "").trim()]),
              ),
            }
          : {}),
        ...(trial ? { trial: true } : {}),
        ...(data.logged_in && projectChoice !== "new" ? { id_project: Number(projectChoice) } : {}),
      });

      setResult(res);

      /* Pakiet platny konczy sie pro forma - przenosimy klienta prosto pod adres
         platnosci. Panel z linkiem zostaje wyrenderowany tak czy tak, wiec gdy
         przegladarka przekierowania nie wykona, dokument nadal jest osiagalny. */
      if (res.status === "payment") window.location.href = res.payment_url;
    } catch (e) {
      if (e instanceof OrderApiError && e.code === 2260049) {
        //backend nazywa brakujace pole w tresci komunikatu - pokazujemy go nad formularzem
        setSubmitError(e.message);
        return;
      }
      setSubmitError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.order}>
        <FormStatus variant="info">{t.orderLoading}</FormStatus>
      </div>
    );
  }

  if (loadError || !data || !pkg) {
    return (
      <div className={styles.order}>
        <FormStatus variant="error">{loadError ?? t.orderLoadFailed}</FormStatus>
        <SubmitButton variant="link" type="button" onClick={onBack}>
          {t.orderBack}
        </SubmitButton>
      </div>
    );
  }

  if (result) {
    return (
      <div className={styles.order}>
        <FormStatus variant="success">{result.message}</FormStatus>
        {/* Konto założone przy zamówieniu jest aktywne, ale nie ma jeszcze hasła - link do
            jego ustawienia idzie mailem. Bez tej informacji klient nie wie, jak wejść
            do panelu, do którego właśnie go odsyłamy. */}
        {!data.logged_in && <FormStatus variant="info">{t.orderPasswordEmail}</FormStatus>}
        {result.status === "payment" && (
          <p className={styles.orderPaymentLink}>
            <a href={result.payment_url}>{t.orderPaymentLink}</a>
          </p>
        )}
        <SubmitButton variant="link" type="button" onClick={onBack}>
          {t.orderBack}
        </SubmitButton>
      </div>
    );
  }

  const trialAvailable = pkg.trial_enabled && pkg.trial_days > 0;
  const submitLabel = trial ? t.orderSubmitTrial : pkg.free ? t.orderSubmitFree : t.orderSubmitPaid;

  /* Pobranie z GUS dokładamy tylko gdy jest klucz API i formularz w ogóle pyta o NIP
     (wariant firmowy) - w przeciwnym razie przycisku nie ma czym obsłużyć. */
  const gus =
    data.gus_available && fields.some((f) => f.name === "billing_nip")
      ? { onLookup: onGusLookup, busy: gusBusy, message: gusMessage }
      : undefined;

  return (
    <div className={styles.order}>
      <header className={styles.orderHeader}>
        <h2 className={styles.orderTitle}>{t.formatOrderTitle(pkg.name ?? "")}</h2>
        <p className={styles.orderSummary}>
          <span className={styles.orderSummaryLabel}>{t.orderSummaryPrice}</span>
          <span className={styles.orderSummaryValue}>
            {pkg.price_brutto} {currency} {t.brutto}
            {pkg.period_label ? ` ${t.formatPeriod(pkg.period_label)}` : ` ${t.oneTime}`}
          </span>
        </p>
        {trial && trialAvailable && (
          <p className={styles.orderSummaryTrial}>{t.formatOrderTrialSummary(pkg.trial_days)}</p>
        )}
      </header>

      <form className={styles.orderForm} onSubmit={onSubmit} noValidate>
        {data.logged_in ? (
          <LoggedInSection
            data={data}
            fields={fields}
            values={values}
            errors={fieldErrors}
            onValue={setValue}
            projectChoice={projectChoice}
            onProject={setProjectChoice}
            gus={gus}
            t={t}
          />
        ) : (
          <GuestSection
            data={data}
            activeType={activeType}
            onAccountType={setAccountType}
            values={values}
            errors={fieldErrors}
            onValue={setValue}
            loginOpen={loginOpen}
            onToggleLogin={() => {
              setLoginOpen((v) => !v);
              setLoginError(null);
            }}
            loginValues={loginValues}
            onLoginValue={(k, v) => setLoginValues((prev) => ({ ...prev, [k]: v }))}
            loginError={loginError}
            loggingIn={loggingIn}
            onLogin={onLogin}
            gus={gus}
            t={t}
          />
        )}

        {/* Bezpłatny test jest OFERTĄ, nie kolejnym polem formularza - dlatego wyróżniony
            boks z ikoną i wprost powiedzianym skutkiem wyboru, a nie checkbox zgubiony
            między danymi do faktury. Zaznaczenie zmienia też etykietę przycisku, więc
            widać, że nie idzie się do płatności.

            Całym boksem jest ETYKIETA checkboxa (`Checkbox` opakowuje children w `<label>`),
            więc kliknięcie gdziekolwiek w nim przełącza wybór - natywnie, bez obsługi
            zdarzeń i bez tracenia dostępności z klawiatury. */}
        {trialAvailable && (
          <Checkbox
            name="trial"
            checked={trial}
            onChange={(e) => setTrial(e.target.checked)}
            className={`${styles.orderTrial} ${trial ? styles.orderTrialOn : ""}`}
          >
            <span className={styles.orderTrialInner}>
              <Icon name="tag" size={40} className={styles.orderTrialIcon} aria-hidden />
              <span className={styles.orderTrialBody}>
                <span className={styles.orderTrialTitle}>
                  {t.formatOrderTrialHeading(pkg.trial_days)}
                </span>
                <span className={styles.orderTrialText}>{t.orderTrialHint}</span>
              </span>
            </span>
          </Checkbox>
        )}

        {/* Dane, które zna wyłącznie klient - dziś adres strony dla usług monitorowanych.
            Backend odrzuci zamówienie bez tej wartości, bo usługa monitorowana bez adresu
            nie miałaby czego sprawdzać.

            Bez `fieldset`/`legend` i bez własnego `placeholder`: etykieta w tym formularzu
            jest PŁYWAJĄCA (label siedzi po inpucie i wjeżdża na niego, dopóki pole jest
            puste - zob. Form.module.css). Własny placeholder zderza się z nią wizualnie,
            a legend powtarzał tę samą nazwę drugi raz. Przykładowy adres idzie do podpowiedzi
            pod polem, gdzie niczego nie zasłania. */}
        {serviceFields.map((field) => (
          <ServiceFieldInput
            key={field.id}
            field={field}
            value={serviceValues[field.id] ?? ""}
            error={serviceErrors[field.id]}
            onValue={setServiceValue}
            t={t}
          />
        ))}

        {data.regulamin && (
          <fieldset className={styles.orderFieldset}>
            <legend className={styles.orderLegend}>{t.orderRegulaminLegend}</legend>
            <Checkbox
              name="regulamin_accept"
              checked={regulaminAccepted}
              onChange={(e) => {
                setRegulaminAccepted(e.target.checked);
                if (e.target.checked) setRegulaminError(null);
              }}
              label={t.orderRegulaminAccept}
              error={regulaminError ?? undefined}
            />
            <SubmitButton
              variant="link"
              type="button"
              onClick={() => setRegulaminOpen((v) => !v)}
              aria-expanded={regulaminOpen}
            >
              {regulaminOpen ? t.orderRegulaminHide : t.orderRegulaminShow}
            </SubmitButton>
            {regulaminOpen && (
              <div
                className={styles.orderRegulaminText}
                dangerouslySetInnerHTML={{ __html: data.regulamin.text }}
              />
            )}
          </fieldset>
        )}

        {submitError && <FormStatus variant="error">{submitError}</FormStatus>}

        <div className={styles.orderActions}>
          <SubmitButton variant="link" type="button" onClick={onBack}>
            {t.orderBack}
          </SubmitButton>
          <SubmitButton loading={submitting}>{submitLabel}</SubmitButton>
        </div>
      </form>
    </div>
  );
}

export type OrderFormProps = {
  idPackage: number;
  /** Waluta cennika z tresci katalogu - podsumowanie pokazuje ja obok kwoty. */
  currency: string;
  t: CCrmProjectManagementTranslations;
  onBack: () => void;
};

/**
 * Wariant dla klienta z sesja: dane rozliczeniowe pobrane z konta - wypelnione, ale
 * EDYTOWALNE - oraz wybor projektu.
 *
 * Zamowienie jest ostatnim momentem, w ktorym te dane trafiaja na pro forme i fakture,
 * wiec klient musi moc je tu poprawic. Poprawka zapisuje sie w koncie, zeby panel i
 * dokument nie rozjechaly sie zaraz po zlozeniu zamowienia.
 */
function LoggedInSection({
  data,
  fields,
  values,
  errors,
  onValue,
  projectChoice,
  onProject,
  gus,
  t,
}: {
  data: OrderFormData;
  fields: OrderFormField[];
  values: Record<string, string>;
  errors: Record<string, string>;
  onValue: (name: string, value: string) => void;
  projectChoice: string;
  onProject: (value: string) => void;
  gus?: { onLookup: () => void; busy: boolean; message: string | null };
  t: CCrmProjectManagementTranslations;
}) {
  const name = userLabel(data.user);

  return (
    <>
      <div className={styles.orderAccount}>
        {name && <p className={styles.orderAccountName}>{t.formatOrderLoggedAs(name)}</p>}
        <p className={styles.orderHint}>{t.orderLoggedDataNote}</p>
      </div>

      <FieldSections
        fields={fields}
        values={values}
        errors={errors}
        onValue={onValue}
        gus={gus}
        t={t}
      />

      <Field label={t.orderProjectLegend} hint={t.orderProjectHint}>
        <Select value={projectChoice} onChange={(e) => onProject(e.target.value)}>
          <option value="new">{t.orderProjectNew}</option>
          {data.projects.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.name ?? `#${p.id}`}
            </option>
          ))}
        </Select>
      </Field>
    </>
  );
}

/**
 * Wariant dla goscia: wybor typu konta, pola wynikajace z konfiguracji jego grupy
 * i skrot do logowania dla kogos, kto konto juz ma.
 */
function GuestSection({
  data,
  activeType,
  onAccountType,
  values,
  errors,
  onValue,
  loginOpen,
  onToggleLogin,
  loginValues,
  onLoginValue,
  loginError,
  loggingIn,
  onLogin,
  gus,
  t,
}: {
  data: OrderFormData;
  activeType: OrderAccountType | null;
  onAccountType: (key: "company" | "private") => void;
  values: Record<string, string>;
  errors: Record<string, string>;
  onValue: (name: string, value: string) => void;
  loginOpen: boolean;
  onToggleLogin: () => void;
  loginValues: { login: string; password: string };
  onLoginValue: (key: "login" | "password", value: string) => void;
  loginError: string | null;
  loggingIn: boolean;
  onLogin: () => void;
  gus?: { onLookup: () => void; busy: boolean; message: string | null };
  t: CCrmProjectManagementTranslations;
}) {
  /* Brak wariantow konta = administrator nie wskazal grup rejestracji w ustawieniach
     podstrony. Zamiast pustego formularza mowimy wprost, ze zamawianie nie dziala. */
  if (!activeType) {
    return <FormStatus variant="warning">{t.orderNoAccountTypes}</FormStatus>;
  }

  return (
    <>
      <div className={styles.orderLogin}>
        <SubmitButton variant="link" type="button" onClick={onToggleLogin} aria-expanded={loginOpen}>
          {loginOpen ? t.orderLoginHide : t.orderHaveAccount}
        </SubmitButton>

        {loginOpen && (
          <div className={styles.orderLoginBox}>
            <Field label={t.orderLoginField}>
              <Input
                type="text"
                autoComplete="username"
                value={loginValues.login}
                onChange={(e) => onLoginValue("login", e.target.value)}
              />
            </Field>
            <Field label={t.orderPasswordField}>
              <Input
                type="password"
                autoComplete="current-password"
                value={loginValues.password}
                onChange={(e) => onLoginValue("password", e.target.value)}
              />
            </Field>
            {loginError && <FormStatus variant="error">{loginError}</FormStatus>}
            <SubmitButton type="button" loading={loggingIn} onClick={onLogin}>
              {t.orderLoginSubmit}
            </SubmitButton>
          </div>
        )}
      </div>

      {data.account_types.length > 1 && (
        <fieldset className={styles.orderFieldset}>
          <legend className={styles.orderLegend}>{t.orderAccountTypeLegend}</legend>
          <div className={styles.orderRadios}>
            {data.account_types.map((type) => (
              <Radio
                key={type.key}
                name="account_type"
                value={type.key}
                checked={activeType.key === type.key}
                onChange={() => onAccountType(type.key)}
                label={type.key === "company" ? t.orderAccountCompany : t.orderAccountPrivate}
              />
            ))}
          </div>
        </fieldset>
      )}

      <FieldSections
        fields={activeType.fields}
        values={values}
        errors={errors}
        onValue={onValue}
        gus={gus}
        t={t}
      />
    </>
  );
}

/**
 * Pola konta pogrupowane w sekcje konfiguracji grupy (dane osobowe / firma / faktura).
 *
 * Wspolne dla goscia i klienta z kontem - roznica jest wylacznie w tym, skad przychodzi
 * lista pol i czy niesie wartosci.
 */
function FieldSections({
  fields,
  values,
  errors,
  onValue,
  gus,
  t,
}: {
  fields: OrderFormField[];
  values: Record<string, string>;
  errors: Record<string, string>;
  onValue: (name: string, value: string) => void;
  /** Pobranie danych z GUS; brak = integracja niedostępna albo wariant bez NIP-u. */
  gus?: { onLookup: () => void; busy: boolean; message: string | null };
  t: CCrmProjectManagementTranslations;
}) {
  return (
    <>
      {SECTIONS.map((section) => {
        const sectionFields = fields.filter((f) => f.section === section);
        if (sectionFields.length === 0) return null;

        /* Pobranie z GUS stoi przy sekcji z NIP-em - to jedyne pole, które trzeba
           wypełnić, żeby akcja miała sens. */
        const showGus = !!gus && sectionFields.some((f) => f.name === "billing_nip");

        return (
          <fieldset key={section} className={styles.orderFieldset}>
            <legend className={styles.orderLegend}>{sectionLabel(section, t)}</legend>

            {sectionHint(section, t) && (
              <p className={styles.orderHint}>{sectionHint(section, t)}</p>
            )}

            {showGus && (
              <div className={styles.orderGus}>
                <SubmitButton
                  variant="link"
                  type="button"
                  loading={gus.busy}
                  loadingLabel={t.orderGusBusy}
                  onClick={gus.onLookup}
                >
                  {t.orderGusFetch}
                </SubmitButton>
                {gus.message && <span className={styles.orderGusMessage}>{gus.message}</span>}
              </div>
            )}
            <div className={styles.orderFields}>
              {sectionFields.map((field) =>
                field.dictionary ? (
                  <DictionaryField
                    key={field.name}
                    field={field}
                    value={values[field.name] ?? ""}
                    error={errors[field.name]}
                    onValue={onValue}
                    t={t}
                  />
                ) : (
                  <Field
                    key={field.name}
                    label={field.label}
                    required={field.required}
                    error={errors[field.name]}
                  >
                    <Input
                      type={field.type}
                      name={field.name}
                      value={values[field.name] ?? ""}
                      invalid={!!errors[field.name]}
                      onChange={(e) => onValue(field.name, e.target.value)}
                    />
                  </Field>
                ),
              )}
            </div>
          </fieldset>
        );
      })}
    </>
  );
}

/**
 * Pole wybierane ze slownika backendu (dzis: kraj).
 *
 * Osobny komponent, bo `useDictionary` to hook - nie da sie go wywolac warunkowo
 * wewnatrz petli po polach. Slownik jest ten sam, z ktorego korzysta edycja danych
 * w panelu klienta (`IIAPI_getPublicDictionary`), wiec zapisana wartosc (`PL`) znaczy
 * wszedzie to samo.
 *
 * Gdy slownik sie nie wczyta, pole zostaje wyborem bez opcji i formularz to pokaze -
 * podmiana na pole tekstowe zapisalaby wpisany z reki kraj, ktorego dokument nie
 * umie zinterpretowac.
 */
function DictionaryField({
  field,
  value,
  error,
  onValue,
  t,
}: {
  field: OrderFormField;
  value: string;
  error?: string;
  onValue: (name: string, value: string) => void;
  t: CCrmProjectManagementTranslations;
}) {
  const { items, loading, error: dictError } = useDictionary(
    field.dictionary as DictionaryType,
  );

  return (
    <Field
      label={field.label}
      required={field.required}
      error={error ?? (dictError ? t.orderDictionaryFailed : undefined)}
    >
      <Select
        name={field.name}
        value={value}
        disabled={loading}
        invalid={!!error}
        onChange={(e) => onValue(field.name, e.target.value)}
      >
        <option value="">{loading ? t.orderDictionaryLoading : t.orderSelectEmpty}</option>
        {items.map((item) => (
          <option key={item.id} value={item.id}>
            {item.name}
          </option>
        ))}
      </Select>
    </Field>
  );
}

/** Etykieta sekcji formularza. */
function sectionLabel(
  section: OrderFormField["section"],
  t: CCrmProjectManagementTranslations,
): string {
  if (section === "company") return t.orderSectionCompany;
  if (section === "billing") return t.orderSectionBilling;
  return t.orderSectionContact;
}

/** Podpis pod nagłówkiem sekcji; pusty tam, gdzie nagłówek mówi wszystko. */
function sectionHint(
  section: OrderFormField["section"],
  t: CCrmProjectManagementTranslations,
): string {
  return section === "company" ? t.orderSectionCompanyHint : "";
}

/**
 * Podpis zalogowanego klienta z danych konta.
 *
 * `IFILTER::filterUserData` zwraca rozny zestaw pol zaleznie od typu konta, wiec
 * czytamy defensywnie i przy braku czegokolwiek uzytecznego nie pokazujemy linii
 * zamiast wyswietlac pusty podpis.
 */
function userLabel(user: Record<string, unknown> | null): string {
  if (!user) return "";
  const parts = ["name", "surname"]
    .map((k) => (typeof user[k] === "string" ? (user[k] as string).trim() : ""))
    .filter(Boolean);

  if (parts.length > 0) return parts.join(" ");
  for (const key of ["company_name", "email", "login"]) {
    if (typeof user[key] === "string" && user[key]) return user[key] as string;
  }
  return "";
}
